import {
  EXPORT_FILE_SOURCE,
  EXPORT_FILE_STATUS,
  formatParisDateTime,
  parisDayBounds,
  type ExportFileListItem,
} from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Download, MoreHorizontal, RotateCcw, Send, Trash2, Upload } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/confirm-dialog.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { useMaintenanceState } from '@/features/settings/use-maintenance-state.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { useOriginFilter } from '@/lib/origin-filter.js';
import {
  downloadExportFile,
  fetchFilesList,
  fetchQueuedFilesCount,
  filesQueryKeys,
  flushFileQueue,
  removeQueuedFile,
  resendExportFile,
} from './api.js';
import { FilesFilters, readFilesFilters } from './filters.js';
import { UploadCsvDialog } from './upload-dialog.js';

const SORTABLE_COLUMNS = ['name', 'store', 'createdAt', 'sentAt', 'status', 'attempts'] as const;
const NUMERIC_COLUMNS = ['attempts'] as const;

function sourceLinkHref(file: ExportFileListItem): string | null {
  if (file.source === 'order' && file.orderName) {
    return `/commandes?${new URLSearchParams({ search: file.orderName }).toString()}`;
  }
  if (file.source === 'invoice' && file.invoiceId) {
    return `/commandes/factures/${file.invoiceId}`;
  }
  return null;
}

const helper = createDataTableColumnHelper<ExportFileListItem>();

const columns = helper.columns([
  helper.accessor('name', {
    header: 'Nom du fichier',
    cell: (info) => (
      <span
        className="block break-all font-medium md:max-w-[15rem] md:truncate md:break-normal xl:max-w-[20rem]"
        title={info.getValue()}
      >
        {info.getValue()}
      </span>
    ),
  }),
  helper.display({
    id: 'source',
    header: 'Source',
    cell: (info) => {
      const file = info.row.original;
      const label =
        file.source === 'order'
          ? file.orderName
          : file.source === 'invoice'
            ? file.invoiceNumber
            : null;
      const href = sourceLinkHref(file);
      return (
        <div className="flex flex-col items-start gap-1">
          <StatusBadge registry={EXPORT_FILE_SOURCE} code={file.source} />
          {label && href ? (
            <a
              className="whitespace-nowrap rounded-sm text-xs font-medium tabular-nums text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              href={href}
            >
              {label}
            </a>
          ) : null}
        </div>
      );
    },
  }),
  helper.accessor('store', {
    header: 'Origine',
    cell: (info) => <span className="uppercase">{info.getValue()}</span>,
  }),
  helper.accessor('createdAt', {
    header: 'Généré le',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.accessor('sentAt', {
    header: 'Envoyé le',
    cell: (info) => {
      const value = info.getValue();
      return (
        <span className="whitespace-nowrap tabular-nums">
          {value ? formatParisDateTime(new Date(value)) : '—'}
        </span>
      );
    },
  }),
  helper.accessor('status', {
    header: 'Statut',
    cell: (info) => <StatusBadge registry={EXPORT_FILE_STATUS} code={info.getValue()} />,
  }),
  helper.accessor('attempts', { header: 'Tentatives' }),
  helper.accessor('lastError', {
    header: 'Dernier message d’erreur',
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <span
          className="block text-xs text-muted-foreground md:line-clamp-2 md:max-w-[16rem]"
          title={value}
        >
          {value}
        </span>
      ) : (
        '—'
      );
    },
  }),
]);

function buildActionsColumn(
  onDownload: (file: ExportFileListItem) => void,
  onResend: (file: ExportFileListItem) => void,
  onRemoveFromQueue: (file: ExportFileListItem) => void,
) {
  return helper.display({
    id: 'actions',
    header: '',
    cell: (info) => {
      const file = info.row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Actions pour ${file.name}`}
            >
              <MoreHorizontal aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={!file.hasContent}
              onSelect={() => {
                onDownload(file);
              }}
            >
              <Download aria-hidden="true" />
              Télécharger
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                onResend(file);
              }}
            >
              <RotateCcw aria-hidden="true" />
              Renvoyer
            </DropdownMenuItem>
            {file.status === 'queued' ? (
              <DropdownMenuItem
                onSelect={() => {
                  onRemoveFromQueue(file);
                }}
              >
                <Trash2 aria-hidden="true" />
                Supprimer de la file
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  });
}

/** Écran Fichiers CSV et envois — `02` §5. */
export function FilesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const rawSearch = useSearch({ strict: false });
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const { origin: store } = useOriginFilter();
  const filters = readFilesFilters(rawSearch);
  const { data: maintenance } = useMaintenanceState();

  const [uploadOpen, setUploadOpen] = useState(false);
  const resendDialog = useConfirmDialog<ExportFileListItem>();
  const removeDialog = useConfirmDialog<ExportFileListItem>();

  const fromBounds = filters.from ? parisDayBounds(filters.from) : undefined;
  const toBounds = filters.to ? parisDayBounds(filters.to) : undefined;

  const listParams = {
    page,
    pageSize: 50,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
    ...(filters.source ? { source: filters.source } : {}),
    ...(filters.ftpAccount ? { ftpAccount: filters.ftpAccount } : {}),
    ...(filters.status.length > 0 ? { status: filters.status } : {}),
    ...(store ? { store } : {}),
    ...(fromBounds ? { from: fromBounds.start.toISOString() } : {}),
    ...(toBounds ? { to: toBounds.end.toISOString() } : {}),
    ...(filters.search ? { search: filters.search } : {}),
  };

  const query = useQuery({
    queryKey: filesQueryKeys.list(listParams),
    queryFn: () => fetchFilesList(listParams),
  });

  const queuedCount = useQuery({
    queryKey: filesQueryKeys.queuedCount(),
    queryFn: fetchQueuedFilesCount,
    refetchInterval: 30_000,
  });

  function invalidateAll(): void {
    void queryClient.invalidateQueries({ queryKey: filesQueryKeys.all });
  }

  const resendMutation = useMutation({
    mutationFn: (file: ExportFileListItem) => resendExportFile(file.id),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Renvoi demandé.');
      invalidateAll();
      resendDialog.close();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (file: ExportFileListItem) => removeQueuedFile(file.id),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Fichier retiré de la file.');
      invalidateAll();
      removeDialog.close();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const flushMutation = useMutation({
    mutationFn: flushFileQueue,
    onSuccess: (result) => {
      toast.success(
        result.testMode ? 'Mode test actif : les fichiers restent en attente.' : result.queuedCount > 0
          ? `Envoi de ${String(result.queuedCount)} fichier(s) demandé (compte${result.ftpAccounts.length > 1 ? 's' : ''} ${result.ftpAccounts.join(', ')}).`
          : 'Aucun fichier en file d’attente.',
      );
      invalidateAll();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const actionsColumn = buildActionsColumn(
    (file) => {
      downloadExportFile(file.id);
    },
    (file) => {
      resendDialog.open(file);
    },
    (file) => {
      removeDialog.open(file);
    },
  );

  return (
    <Page>
      <PageHeader
        title="Fichiers"
        description="Suivre et rejouer les CSV envoyés (ou à envoyer) chez Martelet."
        actions={
          <>
            {(queuedCount.data ?? 0) > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  flushMutation.mutate();
                }}
                isLoading={flushMutation.isPending}
              >
                <Send aria-hidden="true" />
                <span className="tabular-nums">Envoyer la file ({queuedCount.data})</span>
              </Button>
            ) : null}
            <Button
              type="button"
              disabled={maintenance?.enabled ?? false}
              title={
                maintenance?.enabled
                  ? 'Upload manuel désactivé pendant le mode maintenance.'
                  : undefined
              }
              onClick={() => {
                setUploadOpen(true);
              }}
            >
              <Upload aria-hidden="true" />
              Uploader un CSV
            </Button>
          </>
        }
      />

      <DataTable
        columns={[...columns, actionsColumn]}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={50}
        sortBy={sortBy}
        sortDir={sortDir}
        sortableColumnIds={SORTABLE_COLUMNS}
        numericColumnIds={NUMERIC_COLUMNS}
        onPageChange={setPage}
        onSortChange={setSort}
        isLoading={query.isPending}
        isError={query.isError}
        {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        toolbar={<FilesFilters value={filters} onChange={setFilters} />}
        emptyState={{
          title: 'Aucun fichier',
          description: 'Aucun fichier ne correspond à ces filtres.',
        }}
      />

      <UploadCsvDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        disabled={maintenance?.enabled ?? false}
        onUploaded={() => {
          invalidateAll();
        }}
      />

      <ConfirmDialog
        open={resendDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            resendDialog.close();
          }
        }}
        title="Renvoyer ce fichier"
        description={
          resendDialog.target
            ? `Le fichier « ${resendDialog.target.name} » sera renvoyé au FTP Martelet. S’il a déjà été traité, cela peut créer un doublon.`
            : ''
        }
        confirmLabel="Renvoyer"
        isConfirming={resendMutation.isPending}
        onConfirm={() => {
          if (resendDialog.target) {
            resendMutation.mutate(resendDialog.target);
          }
        }}
      />

      <ConfirmDialog
        open={removeDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            removeDialog.close();
          }
        }}
        title="Supprimer de la file"
        description={
          removeDialog.target
            ? `Le fichier « ${removeDialog.target.name} » sera retiré de la file d’attente ; la commande associée repassera au statut « Générée » et pourra être régénérée.`
            : ''
        }
        confirmLabel="Supprimer"
        isConfirming={removeMutation.isPending}
        onConfirm={() => {
          if (removeDialog.target) {
            removeMutation.mutate(removeDialog.target);
          }
        }}
      />
    </Page>
  );
}
